import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db } from '../../firebase/firebaseConfig';
import {
  updateProfile as firebaseUpdateProfile,
} from "firebase/auth";

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';

import { doc, setDoc, getDoc } from 'firebase/firestore';

// ----------------------------------------
// 🔹 Login Thunk
export const loginUser = createAsyncThunk('auth/loginUser', async ({ email, password }) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Ensure agent-details doc exists
  const agentRef = doc(db, 'agent-details', user.uid);
  const agentSnap = await getDoc(agentRef);
  let hasMultipleCompanies = true; // Default to true (show filter)
  let finalDisplayName = user.displayName || '';

  if (!agentSnap.exists()) {
    await setDoc(agentRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      phoneNumber: user.phoneNumber || '',
      createdAt: new Date().toISOString()
    });
  } else {
    // Fetch fields
    const agentData = agentSnap.data();
    hasMultipleCompanies = agentData.hasMultipleCompanies !== "false";
    // If Auth profile has no name, try agent-details fields
    if (!user.displayName) {
      finalDisplayName = agentData.displayName || agentData.name || '';
    }
  }

  // Cache in localStorage for future page loads
  const cacheKey = `hasMultipleCompanies_${user.uid}`;
  localStorage.setItem(cacheKey, hasMultipleCompanies.toString());

  return {
    uid: user.uid,
    email: user.email,
    displayName: finalDisplayName,
    phoneNumber: user.phoneNumber || '',
    hasMultipleCompanies,
  };
});

// ----------------------------------------
// 🔹 Signup Thunk
export const signupUser = createAsyncThunk('auth/signupUser', async ({ email, password }) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Create agent-details doc
  await setDoc(doc(db, 'agent-details', user.uid), {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || '',
    phoneNumber: user.phoneNumber || '',
    createdAt: new Date().toISOString()
  });

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || '',
    phoneNumber: user.phoneNumber || '',
    hasMultipleCompanies: true, // Default to true for new signups
  };
});

// ----------------------------------------
// 🔹 Logout Thunk
export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  await signOut(auth);
});

// ----------------------------------------
// 🔹 Reset Password Thunk
export const resetPassword = createAsyncThunk('auth/resetPassword', async (email) => {
  await sendPasswordResetEmail(auth, email);
});

// ----------------------------------------
// 🔹 Observe Firebase Auth (used on app load)
export const observeUser = () => (dispatch) => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Fetch hasMultipleCompanies from localStorage first (0 reads)
      const cacheKey = `hasMultipleCompanies_${user.uid}`;
      const cachedValue = localStorage.getItem(cacheKey);

      let hasMultipleCompanies = true; // Default to true
      let finalDisplayName = user.displayName || '';

      if (cachedValue !== null) {
        // Use cached value (0 Firebase reads)
        hasMultipleCompanies = cachedValue === 'true';

        // We still need to fetch name if missing, potentially, 
        // but for now let's prioritize consistency. 
        // If we have cache but no name, we might still need a read.
        if (!finalDisplayName) {
          try {
            const agentRef = doc(db, 'agent-details', user.uid);
            const agentSnap = await getDoc(agentRef);
            if (agentSnap.exists()) {
              const agentData = agentSnap.data();
              finalDisplayName = agentData.displayName || agentData.name || '';
            }
          } catch (e) { console.error(e); }
        }
      } else {
        // Fetch from Firebase only if not cached (1 read, one-time ever)
        try {
          const agentRef = doc(db, 'agent-details', user.uid);
          const agentSnap = await getDoc(agentRef);
          if (agentSnap.exists()) {
            const agentData = agentSnap.data();
            hasMultipleCompanies = agentData.hasMultipleCompanies !== "false";
            if (!finalDisplayName) {
              finalDisplayName = agentData.displayName || agentData.name || '';
            }
          }
          // Cache the result for future page loads
          localStorage.setItem(cacheKey, hasMultipleCompanies.toString());
        } catch (error) {
          console.error('Error fetching hasMultipleCompanies:', error);
        }
      }

      const isPhoneUser = user.providerData.some(p => p.providerId === 'phone');

      dispatch(setUser({
        uid: user.uid,
        email: user.email,
        displayName: finalDisplayName,
        phoneNumber: user.phoneNumber || '',
        hasMultipleCompanies,
        isPhoneUser // Helper flag
      }));
    } else {
      dispatch(setUser(null));
    }
  });
};

//  Update Profile Thunk
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async ({ displayName, phoneNumber, location }, { getState }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("No user logged in");

    await firebaseUpdateProfile(user, {
      displayName,
    });

    const agentRef = doc(db, 'agent-details', user.uid);
    await setDoc(agentRef, {
      uid: user.uid,
      email: user.email,
      displayName,
      phoneNumber,
      location,
      updatedAt: new Date().toISOString()
    }, { merge: true });

    return {
      ...getState().auth.user,
      displayName,
      phoneNumber,
      location,
    };
  }
);

// ----------------------------------------
// 🔹 Slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: false,
    error: null,
    authChecked: false,
  },
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
      state.authChecked = true;
    },
    setAuthChecked(state) {
      state.authChecked = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
      });
  },
});

export const { setUser, setAuthChecked } = authSlice.actions;
export default authSlice.reducer;